import { For, Show, createMemo } from 'solid-js';
import type { Finding, FindingPoint, ShowMeAction } from '../../insights/types';
import { evidenceChipLabel } from '../../insights/templates';

export interface InsightCardProps {
  finding: Finding;
  onShowMe?: (action: ShowMeAction) => void;
  /** Show me for one row of a consolidated card. */
  onShowPoint?: (point: FindingPoint) => void;
  /** True while this card's Show-me selection is active. */
  active?: boolean;
  /** Point id whose Show-me selection is active. */
  activePointId?: string | null;
}

export default function InsightCard(props: InsightCardProps) {
  const severityClass = () =>
    `insight-card insight-card--${props.finding.severity}${props.active ? ' insight-card--active' : ''}`;

  const hasPoints = () => (props.finding.points?.length ?? 0) > 0;

  const pointGroups = createMemo(() => {
    const points = props.finding.points ?? [];
    const groups: { title: string; points: FindingPoint[] }[] = [];
    for (const point of points) {
      const title = point.group ?? '';
      const current = groups[groups.length - 1];
      if (current && current.title === title) {
        current.points.push(point);
      } else {
        groups.push({ title, points: [point] });
      }
    }
    return groups;
  });

  return (
    <article
      class={severityClass()}
      data-insight-id={props.finding.id}
      data-insight-active={props.active ? 'true' : undefined}
      aria-current={props.active ? 'true' : undefined}
    >
      <h4 class="insight-card__headline">{props.finding.headline}</h4>
      <p class="insight-card__detail">{props.finding.detail}</p>
      <Show when={hasPoints()}>
        <div class="insight-card__point-groups">
          <For each={pointGroups()}>
            {(group) => (
              <div class="insight-card__point-group">
                <Show when={group.title}>
                  <h5 class="insight-card__point-group-title">{group.title}</h5>
                </Show>
                <ul class="insight-card__points">
                  <For each={group.points}>
                    {(point) => (
                      <li
                        class="insight-card__point"
                        classList={{ 'insight-card__point--active': props.activePointId === point.id }}
                      >
                        <span class="insight-card__point-text">
                          <span class="insight-card__point-label">{point.label}</span>
                          <span class="insight-card__point-comparison">{point.comparison}</span>
                          <span class="insight-card__point-delta">{point.delta}</span>
                        </span>
                        <Show when={!!point.showMe && !!props.onShowPoint}>
                          <button
                            type="button"
                            class="insight-card__show-me insight-card__show-me--inline"
                            classList={{ 'insight-card__show-me--active': props.activePointId === point.id }}
                            aria-pressed={props.activePointId === point.id}
                            onClick={() => props.onShowPoint?.(point)}
                          >
                            Show me
                          </button>
                        </Show>
                      </li>
                    )}
                  </For>
                </ul>
              </div>
            )}
          </For>
        </div>
      </Show>
      <Show when={!hasPoints() && props.finding.evidence.length > 0}>
        <div class="insight-card__evidence">
          <For each={props.finding.evidence}>
            {(ev) => <span class="insight-card__chip">{evidenceChipLabel(ev)}</span>}
          </For>
        </div>
      </Show>
      <Show when={!hasPoints() && !!props.finding.recommendation}>
        <p class="insight-card__recommendation">
          <span class="insight-card__recommendation-label">Try</span>
          {props.finding.recommendation}
        </p>
      </Show>
      <Show when={!hasPoints() && !!props.finding.showMe && !!props.onShowMe}>
        <div class="insight-card__actions">
          <button
            type="button"
            class="insight-card__show-me"
            classList={{ 'insight-card__show-me--active': !!props.active }}
            aria-pressed={props.active ? 'true' : 'false'}
            onClick={() => {
              const action = props.finding.showMe;
              if (action && props.onShowMe) {props.onShowMe(action);}
            }}
          >
            Show me
          </button>
        </div>
      </Show>
    </article>
  );
}
